<template>
  <tr class="warehouse-placeholder-row">
    <td :colspan="type === 'entry' ? 9 : 6">
      <grocery-picker
        v-model="selectedGrocery"
        :groceries="availableGroceries"
        :placeholder="getHint"
        :loading="loading"
        hide-details
        dense
        persistent-hint
        @enter="addGrocery"
        :disable-add="!['purchase', 'entry'].includes(type)"
      />
    </td>
    <td class="text-right">
      <v-btn small color="success" depressed :disabled="!canAdd" @click="addGrocery">
        <v-icon small left>mdi-plus</v-icon>
        {{ $t("add") }}
      </v-btn>
    </td>
  </tr>
</template>

<script>
import hasSnackbar from "@/mixins/hasSnackbar";
import warehouseService from "@/services/warehouse";
import GroceryPicker from "@/components/groceries/fields/GroceryPicker.vue";

export default {
  name: "WarehouseGroceryPlaceholder",
  mixins: [hasSnackbar],
  components: {
    GroceryPicker,
  },
  props: {
    warehouse: {
      type: [Object, null, undefined],
      required: true,
    },
    type: {
      type: String,
      required: true,
      validator: (value) =>
        ["waybill", "purchase", "entry", "writeoff", "return"].includes(value),
    },
    existingGroceries: {
      type: Array,
      default: () => [],
    },
    nextIndex: {
      type: Number,
      default: 1,
    },
  },
  data: () => ({
    selectedGrocery: null,
    loading: false,
    inventory: [],
    inventoryLoaded: false,
    groceries: [],
    groceriesLoaded: false,
  }),
  computed: {
    availableGroceries() {
      // Za purchase i entry koristimo groceries, za ostale inventory
      const isPurchaseOrEntry = ["purchase", "entry"].includes(this.type);

      if (isPurchaseOrEntry) {
        if (!this.groceriesLoaded) return [];

        // Filtriraj artikle koji su već dodati u tabelu
        const existingGroceryIds = this.existingGroceries.map(
          (g) => g.grocery_id || g.id
        );

        return this.groceries
          .filter((item) => !existingGroceryIds.includes(item.id))
          .map((item) => ({
            ...item,
            grocery_id: item.id,
            grocery: item,
            quantity: 0,
          }));
      } else {
        if (!this.inventoryLoaded) return [];

        let filtered = this.inventory.filter(
          (item) => item.warehouse_id === this.warehouse?.id
        );
        // Za waybill, writeoff i return - prikazuj samo namirnice sa zalihama
        if (["waybill", "writeoff", "return"].includes(this.type)) {
          filtered = filtered.filter((item) => item.quantity > 0 || item.total > 0);
        }
        // Filtriraj artikle koji su već dodati u tabelu
        const existingGroceryIds = this.existingGroceries.map(
          (g) => g.grocery_id || g.id
        );
        filtered = filtered.filter(
          (item) => !existingGroceryIds.includes(item.grocery_id)
        );

        return filtered.map((item) => ({
          ...item,
          ...item.grocery,
        }));
      }
    },
    canAdd() {
      return this.selectedGrocery !== null;
    },
    getHint() {
      if (this.loading) {
        return this.$t("loading_inventory_please_wait");
      }
      if (["waybill", "writeoff", "return"].includes(this.type)) {
        return (
          "← " +
          this.$t("warehouse.quick_add_hint") +
          " | " +
          this.$t("only_available_items_shown")
        );
      }
      return "← " + this.$t("warehouse.quick_add_hint");
    },
  },
  methods: {
    async getInventory() {
      this.loading = true;
      try {
        const { data } = await warehouseService.getInventory();
        this.inventory = data;
        this.inventoryLoaded = true;
      } catch (error) {
        this.showErrorSnackbar(error, this.$t("error_loading_inventory"));
      } finally {
        this.loading = false;
      }
    },
    async getGroceries() {
      this.loading = true;
      try {
        const groceryService = await import("@/services/grocery");
        const { data } = await groceryService.default.getGroceries();
        this.groceries = data;
        this.groceriesLoaded = true;
      } catch (error) {
        this.showErrorSnackbar(error, this.$t("error_loading_groceries"));
      } finally {
        this.loading = false;
      }
    },
    addGrocery() {
      if (!this.canAdd) return;

      const isPurchaseOrEntry = ["purchase", "entry"].includes(this.type);

      const grocery = {
        id: isPurchaseOrEntry ? this.selectedGrocery.id : this.selectedGrocery.grocery_id,
        grocery_id: isPurchaseOrEntry
          ? this.selectedGrocery.id
          : this.selectedGrocery.grocery.id,
        code: isPurchaseOrEntry
          ? this.selectedGrocery.code
          : this.selectedGrocery.grocery.code,
        name: isPurchaseOrEntry
          ? this.selectedGrocery.name
          : this.selectedGrocery.grocery.name,
        measure: this.selectedGrocery.measure,
        quantity: isPurchaseOrEntry ? 1 : this.selectedGrocery.quantity || 0,
        warehouse_stock: isPurchaseOrEntry ? 0 : this.selectedGrocery.quantity || 0,
        warehouse_id: this.warehouse.id,
        grocery: isPurchaseOrEntry ? this.selectedGrocery : this.selectedGrocery.grocery,
        warehouse: this.warehouse,
        transaction_type: this.type === "purchase" ? "order" : this.type,
      };

      this.$emit("groceryAdded", grocery);
      this.resetForm();
    },
    resetForm() {
      this.selectedGrocery = null;
    },
  },
  watch: {
    warehouse: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (!newVal) return;

        const isPurchaseOrEntry = ["purchase", "entry"].includes(this.type);

        if (isPurchaseOrEntry && !this.groceriesLoaded) {
          this.getGroceries();
        } else if (!isPurchaseOrEntry && !this.inventoryLoaded) {
          this.getInventory();
        }
      },
    },
    type: {
      immediate: true,
      handler(newVal) {
        if (!this.warehouse) return;

        const isPurchaseOrEntry = ["purchase", "entry"].includes(newVal);

        if (isPurchaseOrEntry && !this.groceriesLoaded) {
          this.getGroceries();
        } else if (!isPurchaseOrEntry && !this.inventoryLoaded) {
          this.getInventory();
        }
      },
    },
  },
};
</script>

<style scoped>
.warehouse-placeholder-row {
  background-color: #fff;
}

.warehouse-placeholder-row:hover {
  background-color: #eeeeee;
}
</style>
